import { fetcher } from "@certn/api";
import { showToast } from "@certn/cascadia";
import { CalendarDate } from "@internationalized/date";
import { useState } from "react";
import { useTranslation } from "react-i18next";

async function downloadFile(url: string, filename: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(blobUrl);
}

function buildCsvDownloadPath(listQuery?: Record<string, string>) {
  const params = new URLSearchParams();
  if (listQuery) {
    for (const [key, value] of Object.entries(listQuery)) {
      if (!value) {
        continue;
      }
      for (const part of value.split(",")) {
        if (part) {
          params.append(key, part);
        }
      }
    }
  }
  const query = params.toString();
  return `/api/internal/client-portal/case/csv-download/${
    query ? `?${query}` : ""
  }`;
}

interface DownloadCSVParams {
  startDate?: CalendarDate;
  endDate?: CalendarDate;
  customerAccountId: string;
  listQuery?: Record<string, string>;
  onSuccess?: () => void;
}

export function useDownloadCSV() {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadCSV = async ({
    startDate,
    endDate,
    customerAccountId,
    listQuery,
    onSuccess,
  }: DownloadCSVParams) => {
    if (!customerAccountId) {
      return;
    }

    setIsDownloading(true);
    try {
      const hasListDateFilter = Boolean(
        listQuery?.order_timeframe_after || listQuery?.order_timeframe_before
      );
      const body: Record<string, string> = {};
      if (!hasListDateFilter && startDate && endDate) {
        body.start_date = startDate.toString();
        body.end_date = endDate.toString();
      }

      const data = await fetcher.submit<{
        csv_presigned_url?: string;
      }>(buildCsvDownloadPath(listQuery), body);

      const downloadUrl = data.csv_presigned_url;

      if (!downloadUrl) {
        throw new Error("No download URL in response");
      }

      const filenameStart =
        listQuery?.order_timeframe_after || startDate?.toString() || "open";
      const filenameEnd =
        listQuery?.order_timeframe_before || endDate?.toString() || "open";

      await downloadFile(
        downloadUrl,
        `cases-${filenameStart}-${filenameEnd}.csv`
      );

      showToast(t("CSV report downloaded successfully"), {
        type: "success",
      });

      onSuccess?.();
    } catch (error) {
      console.error("Error downloading CSV:", error);
      showToast(t("Unable to download CSV report"), {
        type: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    downloadCSV,
    isDownloading,
  };
}
